/**
 * @fileoverview Component for the overall task flow as a part of the Listings Launch flow.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */

import yext from 'goog:yext';

import ExternalAccounts from '/js/yext/externalaccounts/listingslaunch/externalaccounts';
import {ExternalAccountsContext}
  from '/js/yext/externalaccounts/shared/listingslaunch/externalaccountscontext';
import {MatchEntity} from '/src/com/yext/listingslaunchstorm/js/components/matchentity';
import VerifyEntity from '/src/com/yext/listingslaunchstorm/js/components/verification/verifyentity';
import {Welcome} from '/src/com/yext/listingslaunchstorm/js/components/welcome';
import {ListingsLaunchContext} from '/src/com/yext/listingslaunchstorm/js/contexts/listingslaunchcontextprovider';
import {GbpConfiguration} from '/src/com/yext/listingslaunchstorm/js/data/gbpconfiguration';
import {LaunchListingsRequest} from '/src/com/yext/listingslaunchstorm/js/data/launchlistingsrequest';
import {ListingsLaunchStep, ListingsLaunchStepByValue} from '/src/com/yext/listingslaunchstorm/js/data/listingslaunchstep';
import {getVerificationMethodDisplayName} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {VerificationMethod} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {ExternalAccountsLayoutHeader, MatchEntityLayoutHeader, VerifyEntityLayoutHeader, getLaunchStepHeader, getLaunchStepNextButtonText} from '/src/com/yext/listingslaunchstorm/js/util/constants';
import {createListingsLaunchStepKey, getMatchForEntity, getVerificationForEntity, getInitialStep} from '/src/com/yext/listingslaunchstorm/js/util/listingslaunchtaskflowutil';
import {Button} from '/ui/components/button/button';
import {Taskflow} from '/ui/components/taskflow/taskflow';
import {TaskflowMobile} from '/ui/components/taskflow/taskflowMobile';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/listingslaunchtaskflow.module.scss';
import GoogleBusinessProfileLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/google-business-profile-logo.svg';
import CheckedSvg from '/src/com/yext/listingslaunchstorm/public/icons/green-checkmark.svg';
import HourglassSvg from '/src/com/yext/listingslaunchstorm/public/icons/hourglass.svg';
import LightningBolt from '/src/com/yext/listingslaunchstorm/public/icons/lightning-bolt.svg';

/**
 * Component for rendering Taskflow component and handling state updates
 * @param {{onSave: function(LaunchListingsRequest)}} props
 * @return {React.ReactNode}
 */
export function ListingsLaunchTaskFlow({onSave}) {
  const {
    linkedEntities,
    setGoogleGroups,
    matchedListings,
    setMatchedListings,
    verifiedListings,
    setVerifiedListings,
  } = React.useContext(ListingsLaunchContext);
  const {
    publisherIdToExternalAccount,
  } = React.useContext(ExternalAccountsContext);
  // TODO: better way to track linked account changes.
  const linkedAccount = publisherIdToExternalAccount.get(715);
  const lastLinkedAccount = React.useRef(linkedAccount);
  const isSmallViewport = useIsSmallViewport();

  const [currentStep, setCurrentStep] = React.useState(
    getInitialStep(linkedAccount, linkedEntities, matchedListings, verifiedListings),
  );

  const [currentPublisherSetupSection, setCurrentPublisherSetupSection] = React.useState(0);
  const [currentMatchEntitySection, setCurrentMatchEntitySection] = React.useState(0);
  const [currentVerificationSection, setCurrentVerificationSection] = React.useState(0);

  const updateSections = (listingsLaunchStep, state, newSection, index, layoutHeader) => {
    const key = createListingsLaunchStepKey(listingsLaunchStep, index);
    if (state[listingsLaunchStep] !== undefined && state[listingsLaunchStep].some(item => item.id === key)) {
      return state;
    }
    return {
      ...state,
      [listingsLaunchStep]: [
        ...state[listingsLaunchStep],
        {
          id: key,
          layoutHeader: layoutHeader,
          content: newSection,
        },
      ],
    };
  };

  function taskflowStepReducer(state, action) {
    let newSection;
    switch (action.type) {
      case 'update_matched_entities_sections':
        // Catch misuse of the reducer early; the entity referenced by the payload index should
        // always exists. If it does not, perform a noop on the reducer.
        if (!linkedEntities[action.payload]) {
          return state;
        }

        newSection = (<MatchEntity
          entity={linkedEntities[action.payload]}
          key={createListingsLaunchStepKey(ListingsLaunchStep.MATCH_ENTITY, linkedEntities[action.payload].id)}
        />);
        return updateSections(ListingsLaunchStep.MATCH_ENTITY, state, newSection, action.payload, MatchEntityLayoutHeader);
      case 'update_verification_sections':
        // See above; noop if entity does not exist.
        if (!linkedEntities[action.payload]) {
          return state;
        }

        newSection = (
          <VerifyEntity
            entity={linkedEntities[action.payload]}
            key={createListingsLaunchStepKey(ListingsLaunchStep.VERIFICATION, linkedEntities[action.payload].id)}
          />);
        return updateSections(ListingsLaunchStep.VERIFICATION, state, newSection, action.payload, VerifyEntityLayoutHeader);
      case 'remove_all_matches_tasks':
        return {
          ...state,
          [ListingsLaunchStep.MATCH_ENTITY]: [],
        };
      case 'remove_all_verification_tasks':
        return {
          ...state,
          [ListingsLaunchStep.VERIFICATION]: [],
        };
      default:
        return state;
    }
  }

  const googleMyBusinessPublisherId = 715;

  const [taskflowSteps, dispatchTaskflowStepUpdate] = React.useReducer(taskflowStepReducer,
    {
      // External Accounts will always be visible.
      [ListingsLaunchStep.PUBLISHER_SETUP]: [{
        id: createListingsLaunchStepKey(ListingsLaunchStep.PUBLISHER_SETUP, currentPublisherSetupSection),
        layoutHeader: ExternalAccountsLayoutHeader,
        content: (
          <ExternalAccounts
            showSearch
            publisherId={googleMyBusinessPublisherId}
            key={createListingsLaunchStepKey(ListingsLaunchStep.PUBLISHER_SETUP, currentPublisherSetupSection)}
          />),
      }],
      [ListingsLaunchStep.MATCH_ENTITY]: [],
      [ListingsLaunchStep.VERIFICATION]: [],
    },
  );

  const taskflowSections = {
    [ListingsLaunchStep.PUBLISHER_SETUP]: [currentPublisherSetupSection, setCurrentPublisherSetupSection],
    [ListingsLaunchStep.MATCH_ENTITY]: [currentMatchEntitySection, setCurrentMatchEntitySection],
    [ListingsLaunchStep.VERIFICATION]: [currentVerificationSection, setCurrentVerificationSection],
  };

  const [matchEntityTasks, setMatchEntityTasks] = React.useState([]);
  const [verificationTasks, setVerificationTasks] = React.useState([]);

  const createTaskflowSection = (listingsLaunchStep, sectionTitle, content=undefined, isPending=false) => {
    const currentSection = taskflowSections[listingsLaunchStep][0];
    const setCurrentSection = taskflowSections[listingsLaunchStep][1];

    return {
      id: ListingsLaunchStepByValue[listingsLaunchStep],
      title: sectionTitle,
      content: content,
      isSelected: currentStep === listingsLaunchStep && currentSection === 0,
      icon: listingsLaunchStep === ListingsLaunchStep.PUBLISHER_SETUP
        ? <GoogleBusinessProfileLogoSvg />
        : <HourglassSvg />,
      // The task flow component does not have a dedicated "disabled" state; instead, just un-set
      // the click handler when the user should not be able to navigate to that task directly.
      // `isPending` refers to two states in the flow; (1) the step has not been "started", and
      // has only one task saying "pending previous step", and (2) a previous task has not been
      // completed yet (eg, with two incomplete tasks, only the first should be clickable).
      onClick: isPending ? undefined : () => {
        setCurrentStep(listingsLaunchStep);
        setCurrentSection(0);
      },
    };
  };

  const initialTaskflowSections = [
    {
      header: yext.msg('Publisher Setup'),
      children: [createTaskflowSection(
        ListingsLaunchStep.PUBLISHER_SETUP,
        'Google Business Profile',
        <LinkedAccountTaskSectionContent linkedAccount={linkedAccount} />,
      )],
    },
    {
      header: yext.msg('Match Entities'),
      children: matchEntityTasks.length > 0
        ? matchEntityTasks
        : [createTaskflowSection(
          ListingsLaunchStep.MATCH_ENTITY,
          yext.msg('Pending Linked Account'),
          undefined, // content
          true, // isPending
        )],
    },
    {
      header: yext.msg('Verification'),
      children: verificationTasks.length > 0
        ? verificationTasks
        : [createTaskflowSection(
          ListingsLaunchStep.VERIFICATION,
          yext.msg('Pending Matched Entities'),
          undefined, // content
          true, // isPending
        )],
    },
  ];

  React.useEffect(() => {
    // Clear out any matches/verifications dependent on the old account ID.
    if (lastLinkedAccount.current?.id !== linkedAccount?.id) {
      setGoogleGroups([]);
      setMatchedListings([]);
      setVerifiedListings([]);
    }

    lastLinkedAccount.current = linkedAccount;
  }, [linkedAccount, setMatchedListings, setVerifiedListings, setGoogleGroups]);

  // Update the left hand task flow when our matched listings (or linked account) changes. This
  // covers two main cases: (1) a match step has been updated; we should update the task card to
  // reflect its new state (this also covers once a linked account is selected and the user moves
  // to the first match task), and (2) the linked account has been un-set (ie, the user went +
  // to the first step and is selecting a new account), and we should clear and match task cards
  // (and verification cards). The actual matches/verifications are cleared in the above useEffect.
  React.useEffect(() => {
    if (linkedAccount != null) {
      const newMatchEntityTasks = linkedEntities.map((linkedEntity, index) => {
        dispatchTaskflowStepUpdate({type: 'update_matched_entities_sections', payload: index});

        const matchedListing = getMatchForEntity(linkedEntity, matchedListings);

        return {
          id: createListingsLaunchStepKey(ListingsLaunchStep.MATCH_ENTITY, linkedEntities[index].id),
          title: linkedEntity.name,
          content: <MatchTaskSectionContent matchedListing={matchedListing} />,
          isSelected: currentStep === ListingsLaunchStep.MATCH_ENTITY && currentMatchEntitySection === index,
          onClick: () => {
            setCurrentStep(ListingsLaunchStep.MATCH_ENTITY);
            setCurrentMatchEntitySection(index);
          },
        };
      });
      setMatchEntityTasks(newMatchEntityTasks);
    } else {
      dispatchTaskflowStepUpdate({type: 'remove_all_matches_tasks'});
      setMatchEntityTasks([]);
      setVerificationTasks([]);
    }
  }, [currentStep, setCurrentStep, currentMatchEntitySection, linkedEntities, linkedAccount, matchedListings]);

  // See the above comment. This blocks performs the same logic, but for the verification section.
  // If a match is changed, the corresponding verification needs to be reset and its card updated.
  React.useEffect(() => {
    if (linkedAccount != null && linkedEntities.length === matchedListings.length) {
      const newVerificationTasks = linkedEntities.map((linkedEntity, index) => {
        dispatchTaskflowStepUpdate({type: 'update_verification_sections', payload: index});

        const verifiedListing = getVerificationForEntity(linkedEntity, verifiedListings);
        const isNewListing = verifiedListing?.method === VerificationMethod.DISABLED;

        return {
          id: createListingsLaunchStepKey(ListingsLaunchStep.VERIFICATION, linkedEntities[index].id),
          title: linkedEntity.name,
          content: <VerificationTaskSectionContent verifiedListing={verifiedListing} />,
          isSelected: currentStep === ListingsLaunchStep.VERIFICATION && currentVerificationSection === index,
          onClick: isNewListing ? undefined : () => {
            setCurrentStep(ListingsLaunchStep.VERIFICATION);
            setCurrentVerificationSection(index);
          },
          disabled: isNewListing,
        };
      });
      setVerificationTasks(newVerificationTasks);
    } else {
      dispatchTaskflowStepUpdate({type: 'remove_all_verification_tasks'});
      setVerificationTasks([]);
    }
  }, [currentStep, setCurrentStep, currentVerificationSection, linkedEntities, linkedAccount, matchedListings, verifiedListings]);

  const lastStep = Math.max(...Object.values(ListingsLaunchStep));
  const firstStep = Math.min(...Object.values(ListingsLaunchStep));

  function canContinueToNextSection(taskflowStep) {
    const currentSection = taskflowSections[taskflowStep][0];
    const lastSection = taskflowSteps[taskflowStep].length - 1;
    if (currentSection === lastSection) {
      return false;
    }

    switch (taskflowStep) {
      case ListingsLaunchStep.PUBLISHER_SETUP:
        // For now, there is only one publisher setup step (GBP).
        return false;
      case ListingsLaunchStep.MATCH_ENTITY:
        return getMatchForEntity(linkedEntities[currentSection], matchedListings);
      case ListingsLaunchStep.VERIFICATION:
        return getVerificationForEntity(linkedEntities[currentSection], verifiedListings);
      default:
        return false;
    }
  }

  function canContinueToNextStep(taskflowStep) {
    switch (taskflowStep) {
      case ListingsLaunchStep.PUBLISHER_SETUP:
        return linkedAccount != null;
      case ListingsLaunchStep.MATCH_ENTITY:
        return linkedEntities.length === matchedListings.length;
      case ListingsLaunchStep.VERIFICATION:
        return false;
      default:
        return false;
    }
  }

  const findNextSection = (prevSection, taskflowStep) => {
    let nextSection = prevSection + 1;
    const lastSection = taskflowSteps[taskflowStep].length - 1;
    // A verification section can be disabled, skip those
    if (taskflowStep === ListingsLaunchStep.VERIFICATION) {
      while (nextSection <= lastSection) {
        if (!verificationTasks[nextSection].disabled) {
          break;
        }
        nextSection++;
      }
    }
    return (nextSection > lastSection) ? undefined : nextSection;
  };

  const findPrevSection = (currentSection, currentStep) => {
    let prevSection = currentSection - 1;
    // A verification section can be disabled, skip those
    if (currentStep === ListingsLaunchStep.VERIFICATION) {
      while (prevSection >= -1) {
        if (prevSection === -1 || !verificationTasks[prevSection].disabled) {
          break;
        }
        prevSection--;
      }
    }
    return prevSection;
  };

  const nextStep = (() => {
    let nextStep;
    if (canContinueToNextSection(currentStep)) {
      nextStep = currentStep;
    } else if (canContinueToNextStep(currentStep)) {
      nextStep = currentStep + 1;
    } else {
      return undefined;
    }

    const [currentSection, setCurrentSection] = taskflowSections[nextStep];

    const isNextStep = (nextStep != currentStep);
    const nextSection = findNextSection(isNextStep ? -1 : currentSection, nextStep);

    if (nextSection === undefined) {
      return undefined;
    } else {
      return () => {
        setCurrentSection(nextSection);
        isNextStep && setCurrentStep(nextStep);
        resetScroll();
      };
    }
  })();

  const prevStep = () => {
    if (currentStep > firstStep) {
      const currentSection = taskflowSections[currentStep][0];
      const prevSection = findPrevSection(taskflowSections[currentStep][0], currentStep);
      if (prevSection >= 0) {
        taskflowSections[currentStep][1](prevSection);
      } else {
        const sectionsInPrevStep = taskflowSteps[currentStep - 1].length;
        const setCurrentSection = taskflowSections[currentStep - 1][1];
        setCurrentSection(sectionsInPrevStep - 1);
        setCurrentStep(currentStep - 1);
        resetScroll();
      }
    }
  };

  const allComplete =
    matchedListings.length === linkedEntities.length
      && verifiedListings.length === linkedEntities.length;

  const doSave = () => {
    const gbpConfigurations = linkedEntities.map(entity => {
      const match = getMatchForEntity(entity, matchedListings);
      const verification = getVerificationForEntity(entity, verifiedListings);

      let isVerified;
      switch (verification.method) {
        case VerificationMethod.SMS:
        case VerificationMethod.EMAIL:
        case VerificationMethod.PHONE_CALL:
        case VerificationMethod.POSTCARD:
        case VerificationMethod.PREVIOUSLY_VERIFIED:
          isVerified = true;
          break;
        default:
          isVerified = false;
      }

      // External ID and Group ID will always be present at this point.
      return new GbpConfiguration({
        entityId: entity.id,
        externalId: match.externalId || '',
        linkedAccountId: linkedAccount.id,
        groupId: match.groupId || '',
        isVerified,
      });
    });

    onSave(new LaunchListingsRequest({
      publisherId: 715,
      gbpConfigurations,
    }));
  };

  const showSave = nextStep === undefined && allComplete;
  const isEndOfStep = step => {
    if (ListingsLaunchStep.PUBLISHER_SETUP === step) {
      return true;
    } else if (ListingsLaunchStep.MATCH_ENTITY === step) {
      return currentMatchEntitySection === matchEntityTasks.length - 1;
    } else { // ListingsLaunchStep.VERIFICATION === currentStep
      return currentVerificationSection === verificationTasks.length - 1;
    }
  };

  function getPrimaryButtonText() {
    const nextStepButtonText = getLaunchStepNextButtonText(currentStep);
    if (isSmallViewport) {
      if (showSave) {
        return yext.msg('Save');
      } else {
        return isEndOfStep(currentStep) ? nextStepButtonText : yext.msg('Continue to Next Entity');
      }
    } else {
      return showSave ? yext.msg('Save') : yext.msg('Continue');
    }
  }

  const getActionBarCtas = () => {
    const ctas = [];
    const primaryButtonText = getPrimaryButtonText();
    ctas.push(
      <Button
        context="primary"
        fullWidth={isSmallViewport}
        disabled={nextStep === undefined && !allComplete}
        onClick={showSave ? doSave : nextStep}
        key={`${currentStep}-${taskflowSections[currentStep]}-continue`}
        pendoId="listings-launch-flow-actions-bar-continue"
      >
        {primaryButtonText}
      </Button>,
    );
    if (isSmallViewport && ListingsLaunchStep.PUBLISHER_SETUP !== currentStep) {
      ctas.push(
        <Button
          chromeless={true}
          fullWidth={isSmallViewport}
          disabled={prevStep === undefined}
          onClick={(currentStep > firstStep) ? () => prevStep() : undefined}
          key={`${currentStep}-${taskflowSections[currentStep]}-back`}
          pendoId="listings-launch-flow-actions-bar-back"
        >
          {yext.msg('Back')}
        </Button>,
      );
    }
    return ctas;
  };
  const actionBarCtas = getActionBarCtas();
  const actionsBarProps = {
    ctas: actionBarCtas,
    onBack: (currentStep > firstStep) ? () => prevStep() : undefined,
    padding: true,
    pendoId: 'listings-launch-flow',
  };

  const flattenedTaskflowSteps = React.useMemo(() => {
    return [].concat(...Object.values(taskflowSteps));
  }, [taskflowSteps]);

  // TODO: temporary to prevent page from erroring while loading initial state.
  if (!taskflowSteps[currentStep].length) {
    return null;
  }

  if (linkedEntities.length === 0) {
    return (
      <Welcome />
    );
  }

  function getMobilePage(step, currentTask) {
    if (!(step in Object.keys(ListingsLaunchStepByValue))
        || currentTask > taskflowSteps[step].length
        || currentTask < 0) {
      return undefined;
    }
    const task = taskflowSteps[step][currentTask];
    const header = getLaunchStepHeader(step);
    return buildMobileTask(header, task);
  }

  // only the current page/header is needed to render the content. But to make sure the taskflow
  // stepper shows the correct number of steps / highlights the current step, we fill the following
  // arrays with the required number of values (i.e. # of steps in launch flow)
  const numSteps = Object.keys(ListingsLaunchStep).length;
  const mobilePages = new Array(numSteps).fill(
    getMobilePage(currentStep, taskflowSections[currentStep][0]));
  const mobileMultistepProps = {
    steps: [yext.msg('Link Accounts'), yext.msg('Matching'), yext.msg('Verification')],
    selectedStepIndex: currentStep,
    compactLayout: true,
  };

  return (
    <>
      {isSmallViewport
        ? <div className={styles.mobileTaskflow}>
          <TaskflowMobile
            headerLogo={<div className={styles.mobileHeaderLogo}><LightningBolt/></div>}
            multistep={mobileMultistepProps}
            pages={mobilePages}
            CTAs={actionBarCtas}
          />
        </div>
        : <Taskflow
          sections={initialTaskflowSections}
          pages={flattenedTaskflowSteps}
          actionsBar={actionsBarProps}
          selectedPageId={ListingsLaunchStepByValue[currentStep] + '-' + taskflowSections[currentStep][0]}
          padding={true}
        />
      }
    </>
  );
}

function LinkedAccountTaskSectionContent({linkedAccount}) {
  return (
    <TaskSectionContent>
      <div>
        <span className={styles.subItemIcon}>
          {linkedAccount ? <CheckedSvg /> : <HourglassSvg />}
        </span>
        {yext.msg('Linked Accounts')}
      </div>
    </TaskSectionContent>
  );
}

function MatchTaskSectionContent({matchedListing}) {
  return !matchedListing ? null : (
    <TaskSectionContent>
      {matchedListing.displayAddress}
    </TaskSectionContent>
  );
}

function VerificationTaskSectionContent({verifiedListing}) {
  return !verifiedListing ? null : (
    <TaskSectionContent>
      {getVerificationMethodDisplayName(verifiedListing.method)}
    </TaskSectionContent>
  );
}

function TaskSectionContent({children}) {
  return (
    <div className={styles.subItemsContainer}>
      {children}
    </div>
  );
}

function buildMobileTask(header, task) {
  if (!task) {
    return undefined;
  }
  return {
    icon: header.icon,
    title: header.title,
    description: header.description,
    resourceLink: header.resourceLink,
    headerCTAs: undefined,
    contents: [{contentHeader: '', contentBody: task.content}],
  };
}

function resetScroll() {
  window.scrollTo(0, 0);
}
